/**
 * NDAAnnexureCataloguePicker — catalogue-style multi-select picker for
 * Annexure A confidential-information items.
 *
 * 2026-02 — enhancement to the production-ready NDA Builder.
 *
 * Modeled after the existing Proposal Product Catalogue UX pattern
 * (`CataloguePickerDialog.jsx`) so operators enjoy a familiar interaction
 * pattern.  Unlike the Proposal catalogue this picker deals with
 * confidential-information categories only — there are NO price / GST /
 * quantity / licence fields anywhere (§16 · non-commercial NDA contract).
 *
 * Props:
 *   open                — boolean, dialog visibility
 *   onOpenChange(v)     — close handler
 *   ndaType             — "Data" | "Software" — drives the fetch filter
 *   existingMasterIds   — Set<string> of item_master_id values already on
 *                         this NDA.  Rows in this set are surfaced with an
 *                         "Already added" badge and CANNOT be selected (§6).
 *   onCommit(items)     — invoked with an array of { item_master_id,
 *                         description_override?, disclosure_mode? } — parent
 *                         POSTs this to /billing/nda/{id}/item/bulk-add and
 *                         closes the picker on success.
 *
 * Behaviour:
 *   • Only Active items matching `ndaType` are fetched — cross-type items
 *     are impossible to reach, but backend also enforces (§3).
 *   • Multi-select via row checkboxes.  Search preserves in-progress
 *     selections (rows scrolled out of view remain checked).
 *   • Filter-by-group dropdown surfaces the distinct `item_group` values.
 *   • Selected counter, Select-all-filtered, Clear helpers.
 *   • Footer: Cancel + "Add Selected (N)".
 */
import { useEffect, useMemo, useState } from "react";
import {
    MagnifyingGlass, Check, Plus, X, Shield, ListChecks,
    CaretDown, CaretRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function NDAAnnexureCataloguePicker({
    open,
    onOpenChange,
    ndaType,
    existingMasterIds,
    onCommit,
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState("");
    const [groupFilter, setGroupFilter] = useState("");
    // Map<item_master_id, true>
    const [selected, setSelected] = useState({});
    // Map<parent_category, true> — true = collapsed
    const [collapsedParents, setCollapsedParents] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Load whenever the picker re-opens.  Query + selection are also reset.
    useEffect(() => {
        if (!open) return;
        Promise.resolve().then(() => {
            setQuery("");
            setGroupFilter("");
            setSelected({});
            setCollapsedParents({});
        });
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(
                    `/billing/nda-item-master/list?nda_type=${encodeURIComponent(ndaType)}`,
                );
                // Frontend guard §3 — backend also enforces.  Only Active +
                // exact-type items surface.
                const rows = (data.items || [])
                    .filter((r) => r.is_active !== false)
                    .filter((r) => r.nda_type === ndaType);
                setItems(rows);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load confidential-information catalogue"));
                setItems([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [open, ndaType]);

    const groups = useMemo(() => {
        const s = new Set();
        for (const it of items) {
            const g = (it.item_group || "").trim();
            if (g) s.add(g);
        }
        return Array.from(s).sort();
    }, [items]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter((it) => {
            if (groupFilter && (it.item_group || "") !== groupFilter) return false;
            if (!q) return true;
            return (
                (it.item_name || "").toLowerCase().includes(q) ||
                (it.item_code || "").toLowerCase().includes(q) ||
                (it.item_group || "").toLowerCase().includes(q) ||
                (it.description || "").toLowerCase().includes(q) ||
                (it.parent_category || "").toLowerCase().includes(q) ||
                (it.technical_name || "").toLowerCase().includes(q) ||
                (it.primary_key || "").toLowerCase().includes(q)
            );
        });
    }, [items, query, groupFilter]);

    // Group filtered items by parent_category, keyed in stable insertion
    // order (matches backend sort: nda_type / display_order / name).  Items
    // without a parent_category go into the "" bucket which renders as
    // "Other" at the top for legacy backwards-compat display.
    const parentBuckets = useMemo(() => {
        const buckets = new Map(); // parent -> [items]
        for (const it of filtered) {
            const p = (it.parent_category || "").trim();
            if (!buckets.has(p)) buckets.set(p, []);
            buckets.get(p).push(it);
        }
        return Array.from(buckets.entries()); // [[parent, items], ...]
    }, [filtered]);

    const totalParents = useMemo(() => {
        const s = new Set();
        for (const it of items) s.add((it.parent_category || "").trim());
        return s.size;
    }, [items]);
    const isHierarchical = totalParents > 1;

    const selectedCount = useMemo(
        () => Object.values(selected).filter(Boolean).length,
        [selected],
    );

    const isAlreadyAdded = (id) =>
        existingMasterIds instanceof Set && existingMasterIds.has(id);

    const toggleRow = (id) => {
        if (isAlreadyAdded(id)) return; // §6 backend-authoritative, FE-safe
        setSelected((s) => {
            const next = { ...s };
            if (next[id]) delete next[id];
            else next[id] = true;
            return next;
        });
    };

    const clearAll = () => setSelected({});

    const selectableFiltered = useMemo(
        () => filtered.filter((it) => !isAlreadyAdded(it.id)),
        [filtered, existingMasterIds],
    );

    const selectAllFiltered = () => {
        setSelected((s) => {
            const next = { ...s };
            for (const it of selectableFiltered) next[it.id] = true;
            return next;
        });
    };

    const selectableForParent = (parent) =>
        parentBuckets.find(([p]) => p === parent)?.[1]
            ?.filter((it) => !isAlreadyAdded(it.id)) || [];

    const selectAllInParent = (parent) => {
        const rows = selectableForParent(parent);
        setSelected((s) => {
            const next = { ...s };
            for (const it of rows) next[it.id] = true;
            return next;
        });
    };

    const clearInParent = (parent) => {
        const rows = selectableForParent(parent);
        setSelected((s) => {
            const next = { ...s };
            for (const it of rows) delete next[it.id];
            return next;
        });
    };

    const parentSelectedCount = (parent) =>
        selectableForParent(parent).reduce(
            (n, it) => (selected[it.id] ? n + 1 : n),
            0,
        );

    const toggleParentCollapsed = (parent) => {
        setCollapsedParents((s) => {
            const next = { ...s };
            if (next[parent]) delete next[parent];
            else next[parent] = true;
            return next;
        });
    };

    const filteredCheckedCount = selectableFiltered.reduce(
        (n, it) => (selected[it.id] ? n + 1 : n),
        0,
    );

    const commit = async () => {
        // Preserve catalogue display order for predictability.
        const ordered = items
            .filter((it) => selected[it.id] && !isAlreadyAdded(it.id))
            .map((it) => ({ item_master_id: it.id }));
        if (ordered.length === 0) return;
        setSubmitting(true);
        try {
            await onCommit(ordered);
            // Parent closes the picker on success — but reset selection
            // proactively so a re-open starts clean.
            setSelected({});
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[60] bg-slate-900/40 flex items-start justify-center pt-16 px-4"
            onClick={() => onOpenChange(false)}
            data-testid="nda-annexure-picker-overlay"
        >
            <div
                className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Confidential information catalogue"
                data-testid="nda-annexure-picker-dialog"
            >
                {/* ─── Title bar ─── */}
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 bg-slate-50/60">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <Shield size={18} weight="duotone" className="text-slate-600 shrink-0" />
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-slate-900 truncate">
                                Confidential Information Catalogue
                            </div>
                            <div className="text-[11px] text-slate-500 truncate">
                                {ndaType} NDA · pick one or more categories proposed to be disclosed for evaluation
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="text-slate-400 hover:text-slate-900 shrink-0"
                        aria-label="Close picker"
                        data-testid="nda-annexure-picker-close-btn"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* ─── Search + Group filter ─── */}
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                        <MagnifyingGlass size={14} className="text-slate-400 shrink-0" />
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search categories…"
                            className="flex-1 text-sm focus:outline-none bg-transparent"
                            data-testid="nda-annexure-picker-search"
                        />
                    </div>
                    {groups.length > 0 && (
                        <select
                            value={groupFilter}
                            onChange={(e) => setGroupFilter(e.target.value)}
                            className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white"
                            data-testid="nda-annexure-picker-group-filter"
                        >
                            <option value="">All groups</option>
                            {groups.map((g) => (
                                <option key={g} value={g}>{g}</option>
                            ))}
                        </select>
                    )}
                </div>

                {/* ─── Selection toolbar ─── */}
                <div
                    className="px-5 py-2 border-b border-slate-100 bg-slate-50/40 flex items-center gap-3 text-[11px]"
                    data-testid="nda-annexure-picker-toolbar"
                >
                    <ListChecks size={12} className="text-slate-500" />
                    <span
                        className={
                            "font-semibold " +
                            (selectedCount > 0 ? "text-amber-700" : "text-slate-500")
                        }
                        data-testid="nda-annexure-picker-selected-count"
                    >
                        {selectedCount === 0
                            ? "Nothing selected yet"
                            : `${selectedCount} selected`}
                    </span>
                    <span className="text-slate-300">·</span>
                    <button
                        type="button"
                        onClick={selectAllFiltered}
                        disabled={
                            selectableFiltered.length === 0 ||
                            filteredCheckedCount === selectableFiltered.length
                        }
                        className="text-indigo-600 hover:text-indigo-800 disabled:text-slate-300 disabled:cursor-not-allowed"
                        data-testid="nda-annexure-picker-select-all-btn"
                    >
                        Select all
                        {(query.trim() || groupFilter) &&
                        selectableFiltered.length !== items.length
                            ? ` (${selectableFiltered.length} matching)`
                            : ""}
                    </button>
                    {selectedCount > 0 && (
                        <>
                            <span className="text-slate-300">·</span>
                            <button
                                type="button"
                                onClick={clearAll}
                                className="text-slate-500 hover:text-slate-900"
                                data-testid="nda-annexure-picker-clear-btn"
                            >
                                Clear
                            </button>
                        </>
                    )}
                    <span className="ml-auto text-slate-500">
                        {items.length} total · {filtered.length} shown
                    </span>
                </div>

                {/* ─── Scrollable item list ─── */}
                <div
                    className="overflow-y-auto flex-1"
                    data-testid="nda-annexure-picker-list"
                >
                    {loading ? (
                        <div
                            className="px-5 py-8 text-center text-sm text-slate-500"
                            data-testid="nda-annexure-picker-loading"
                        >
                            Loading catalogue…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div
                            className="px-5 py-8 text-center text-sm text-slate-500"
                            data-testid="nda-annexure-picker-empty"
                        >
                            {query.trim() || groupFilter
                                ? "No items match the current filters."
                                : `No active ${ndaType} NDA items are configured.  Ask an admin to seed the Item Master.`}
                        </div>
                    ) : (
                        parentBuckets.map(([parent, rows]) => {
                            const parentKey = parent || "__unclassified__";
                            const parentLabel = parent || "Other Confidential Information";
                            const collapsed = !!collapsedParents[parentKey];
                            const selectable = rows.filter((it) => !isAlreadyAdded(it.id));
                            const selCount = parentSelectedCount(parent);
                            const allSelected =
                                selectable.length > 0 && selCount === selectable.length;
                            return (
                                <div
                                    key={parentKey}
                                    className="border-b border-slate-200"
                                    data-testid={`nda-annexure-picker-parent-${parentKey}`}
                                >
                                    {/* Parent header */}
                                    <div
                                        className={
                                            "flex items-center gap-2 px-4 py-2 sticky top-0 z-[1] " +
                                            (isHierarchical
                                                ? "bg-slate-100 border-b border-slate-200"
                                                : "bg-slate-50")
                                        }
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleParentCollapsed(parentKey)}
                                            className="text-slate-500 hover:text-slate-900 shrink-0"
                                            aria-label={collapsed ? "Expand group" : "Collapse group"}
                                            data-testid={`nda-annexure-picker-parent-toggle-${parentKey}`}
                                        >
                                            {collapsed ? <CaretRight size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                                        </button>
                                        <span
                                            className="text-xs font-semibold uppercase tracking-wide text-slate-700"
                                            data-testid={`nda-annexure-picker-parent-label-${parentKey}`}
                                        >
                                            {parentLabel}
                                        </span>
                                        <span className="text-[10px] text-slate-500">
                                            {rows.length} item{rows.length === 1 ? "" : "s"}
                                            {selCount > 0 ? ` · ${selCount} selected` : ""}
                                        </span>
                                        <div className="ml-auto flex items-center gap-2 text-[11px]">
                                            <button
                                                type="button"
                                                onClick={() => selectAllInParent(parent)}
                                                disabled={allSelected || selectable.length === 0}
                                                className="text-indigo-600 hover:text-indigo-800 disabled:text-slate-300 disabled:cursor-not-allowed"
                                                data-testid={`nda-annexure-picker-parent-select-all-${parentKey}`}
                                            >
                                                Select all
                                            </button>
                                            {selCount > 0 && (
                                                <>
                                                    <span className="text-slate-300">·</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => clearInParent(parent)}
                                                        className="text-slate-500 hover:text-slate-900"
                                                        data-testid={`nda-annexure-picker-parent-clear-${parentKey}`}
                                                    >
                                                        Clear
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {/* Children */}
                                    {!collapsed && rows.map((it) => {
                                        const alreadyAdded = isAlreadyAdded(it.id);
                                        const isChecked = !!selected[it.id];
                                        return (
                                            <label
                                                key={it.id}
                                                className={
                                                    "flex items-start gap-3 px-5 py-2.5 border-b border-slate-100 transition-colors " +
                                                    (alreadyAdded
                                                        ? "bg-slate-50/60 cursor-not-allowed opacity-70"
                                                        : isChecked
                                                        ? "bg-amber-50/60 hover:bg-amber-50 cursor-pointer"
                                                        : "hover:bg-slate-50 cursor-pointer")
                                                }
                                                data-testid={`nda-annexure-pick-row-${it.id}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked || alreadyAdded}
                                                    disabled={alreadyAdded}
                                                    onChange={() => toggleRow(it.id)}
                                                    className="mt-1 h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 focus:ring-1 cursor-pointer disabled:cursor-not-allowed"
                                                    data-testid={`nda-annexure-pick-checkbox-${it.id}`}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-sm font-semibold text-slate-900">
                                                            {it.item_name}
                                                        </span>
                                                        {it.item_group && (
                                                            <span
                                                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                                                data-testid={`nda-annexure-pick-group-${it.id}`}
                                                            >
                                                                {it.item_group}
                                                            </span>
                                                        )}
                                                        {alreadyAdded && (
                                                            <span
                                                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-800 border border-emerald-200 shrink-0"
                                                                title="Already on this NDA"
                                                                data-testid={`nda-annexure-pick-already-added-${it.id}`}
                                                            >
                                                                <Check size={9} weight="bold" /> Already added
                                                            </span>
                                                        )}
                                                    </div>
                                                    {it.description && (
                                                        <div className="text-xs text-slate-500 mt-1 whitespace-pre-line">
                                                            {it.description}
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-3 text-[10px] text-slate-400 mt-0.5 font-mono">
                                                        <span>{it.item_code}</span>
                                                        {it.technical_name && (
                                                            <span data-testid={`nda-annexure-pick-tech-${it.id}`}>
                                                                <span className="text-slate-300">tech · </span>{it.technical_name}
                                                            </span>
                                                        )}
                                                        {it.primary_key && (
                                                            <span data-testid={`nda-annexure-pick-pk-${it.id}`}>
                                                                <span className="text-slate-300">key · </span>{it.primary_key}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* ─── Footer ─── */}
                <div className="px-5 py-3 border-t border-slate-200 bg-white flex items-center justify-between gap-3">
                    <span
                        className="text-[11px] text-slate-500"
                        data-testid="nda-annexure-picker-footer-hint"
                    >
                        Snapshots freeze at add-time — later master edits do not alter this NDA.
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="px-3 py-1.5 text-xs rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
                            data-testid="nda-annexure-picker-cancel-btn"
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={commit}
                            disabled={selectedCount === 0 || submitting}
                            className="px-3 py-1.5 text-xs rounded font-semibold bg-slate-900 hover:bg-slate-800 text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                            data-testid="nda-annexure-picker-add-selected-btn"
                        >
                            <Plus size={12} weight="bold" />
                            {submitting
                                ? "Adding…"
                                : `Add Selected${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
